import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { LuUserRound } from 'react-icons/lu';
import useStaffStore from '../../../store/staffStore';
import useSchoolStore from '../../../store/schoolStore';
import { getStaffRole, getStaffDepartment, formatPhoneNumber, formatDate } from '../../../utils/staffUtils';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import PageHeader from '../../../components/common/PageHeader';
import InfoGrid, { InfoItem } from '../../../components/common/InfoGrid';
import Avatar from '../../../components/common/Avatar';
import Badge from '../../../components/common/Badge';
import Notice from '../../../components/common/Notice';
import EmptyState from '../../../components/common/EmptyState';
import ErrorWidget from '../../../components/common/ErrorWidget';
import LoadingMessage from '../../../components/common/LoadingMessage';
import UnsupportedFeatureDialog from '../../../components/utils/UnsupportedFeatureDialog';

const formatGender = (gender) => {
  if (gender === 'M') return 'Male';
  if (gender === 'F') return 'Female';
  return gender;
};

/**
 * One staff member's details, read-only. The staff member is taken from the list loaded on the
 * Staff page (useStaffStore.staff); editing is done in the SchoolLine app.
 */
const StaffDetailsPage = () => {
  const { staffId } = useParams();
  const [showUnsupportedDialog, setShowUnsupportedDialog] = useState(false);
  const [staffData, setStaffData] = useState(null);

  const { staff, isStaffLoading, staffError } = useStaffStore();
  const { schoolId } = useSchoolStore();

  useEffect(() => {
    if (staff && staff.length > 0 && staffId) {
      const staffMember = staff.find(staffItem => staffItem.id === staffId || staffItem.userId === staffId);
      setStaffData(staffMember);
    }
  }, [staff, staffId]);

  const back = { to: `/dash/${schoolId}/staff`, label: 'Staff' };

  if (isStaffLoading) {
    return <LoadingMessage message="Loading staff details…" />;
  }

  if (staffError) {
    return (
      <>
        <PageHeader title="Staff details" back={back} />
        <ErrorWidget error={{ message: staffError }} title="Could not load the staff member" />
      </>
    );
  }

  if (!staffData) {
    return (
      <>
        <PageHeader title="Staff details" back={back} />
        <Card>
          <EmptyState
            icon={<LuUserRound />}
            title="Staff member not found"
            message="Open the Staff page and pick the staff member again."
          />
        </Card>
      </>
    );
  }

  const teacher = staffData.teacher || {};

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={staffData.name || 'Staff details'}
        back={back}
        actions={
          <Button variant="secondary" onClick={() => setShowUnsupportedDialog(true)}>
            Edit details
          </Button>
        }
      />

      <Notice tone="info" className="mb-5">
        Staff details can be viewed here. To change them, use the SchoolLine app.
      </Notice>

      <Card className="mb-5">
        <div className="flex items-center gap-5">
          <Avatar name={staffData.name} src={staffData.profileUrl} size="xl" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-ui-ink">{staffData.name || '—'}</h2>
              <Badge tone={staffData.blocked ? 'red' : 'green'}>{staffData.blocked ? 'Blocked' : 'Active'}</Badge>
            </div>
            <InfoGrid columns={4} className="mt-3">
              <InfoItem label="Role" value={getStaffRole(staffData)} />
              <InfoItem label="Department" value={getStaffDepartment(staffData)} />
              <InfoItem label="Staff code" value={teacher.staffCode} />
              <InfoItem label="Staff ID" value={staffData.userId} />
            </InfoGrid>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card title="Personal">
          <InfoGrid columns={2}>
            <InfoItem label="Full name" value={staffData.name} />
            <InfoItem label="Gender" value={formatGender(teacher.gender)} />
            <InfoItem label="Date of birth" value={formatDate(teacher.dob)} />
            <InfoItem label="Aadhaar number" value={teacher.aadhaarNo} />
            <InfoItem label="Email" value={teacher.gmail} />
            <InfoItem label="Mobile number" value={formatPhoneNumber(teacher.mobNo)} />
            <InfoItem label="PAN number" value={teacher.panNo} />
          </InfoGrid>
        </Card>

        <Card title="Work">
          <InfoGrid columns={2}>
            <InfoItem label="Staff type" value={teacher.staffType} />
            <InfoItem label="Staff code" value={teacher.staffCode} />
            <InfoItem label="Join date" value={formatDate(teacher.joinDate)} />
            <InfoItem label="Last school" value={teacher.lastSchool} />
            <InfoItem label="Other school experience" value={teacher.otherSchoolExp} />
            <InfoItem label="Academic qualification" value={teacher.academicQualification} />
            <InfoItem label="Teaching qualification" value={teacher.teachingQualification} />
            <InfoItem label="Other qualification" value={teacher.otherQualification} />
          </InfoGrid>
        </Card>

        <Card title="Address">
          <InfoGrid columns={2}>
            <InfoItem label="House name" value={teacher.houseName} />
            <InfoItem label="Locality" value={teacher.locality} />
            <InfoItem label="Place" value={teacher.place} />
            <InfoItem label="Post office" value={teacher.postOffice} />
            <InfoItem label="Taluk" value={teacher.taluk} />
            <InfoItem label="District" value={teacher.district} />
            <InfoItem label="PIN code" value={teacher.pinCode} />
          </InfoGrid>
        </Card>

        <Card title="Bank and statutory">
          <InfoGrid columns={2}>
            <InfoItem label="Bank account number" value={teacher.bankAccNo} />
            <InfoItem label="Bank name" value={teacher.bankName} />
            <InfoItem label="IFSC code" value={teacher.ifsc} />
            <InfoItem label="ESIC number" value={teacher.esicNo} />
            <InfoItem label="EPF UAN" value={teacher.epfUan} />
          </InfoGrid>
        </Card>

        <Card title="Account" className="lg:col-span-2">
          <InfoGrid columns={4}>
            <InfoItem label="User ID" value={staffData.userId} />
            <InfoItem label="Account type" value={staffData.type === 0 ? 'Staff/Teacher' : 'Other'} />
            <InfoItem label="Registered number" value={staffData.registeredNumber?.toString()} />
            <InfoItem label="Account status" value={staffData.blocked ? 'Blocked' : 'Active'} />
            {staffData.blockReason && <InfoItem label="Block reason" value={staffData.blockReason} />}
          </InfoGrid>
        </Card>
      </div>

      <UnsupportedFeatureDialog
        isOpen={showUnsupportedDialog}
        onClose={() => setShowUnsupportedDialog(false)}
        feature="Staff Data Editing"
      />
    </div>
  );
};

export default StaffDetailsPage;
